import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { useAppSelector } from '../../../../store/hooks'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { isSessionStillResolving, selectAuthStateSlice } from '../../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'

import { FrontendUrls } from '../../../../types/urls'
import { CrispSegmentsEnum } from '../../../../types/crisp'
import { getDashboardUrl } from '../../../../util/get-dashboard-url'
import { setCrispSessionInfo } from '../../../../util/set-crisp-session-info'
import { BrowserRuntime } from '../../../../util/openid/browser-runtime'
import { saveSubscriptionPeriodFromQuery } from '../../../signup/subscription-period'
import { setSigninEmail, trackSignin } from '../analytics'
import { ISigninQueryParams } from '../query-params'

/**
 * True when this tab is the ADP callback popup rather than the sign-in page. The opener
 * is waiting for the code, so the page must hand it over and close without rendering a
 * flow at all.
 */
export const isAdpCallbackPopup = (queryParams: ISigninQueryParams): boolean => Boolean(queryParams.code) && typeof window !== 'undefined' && Boolean(window.opener)

export interface IUseSigninBootstrapParams {
  queryParams: ISigninQueryParams
  search: string
  redirectUrl: string
  /** The remembered address, so the events fired before any lookup already carry it. */
  initialEmail: string
}

/**
 * Everything the page does because it was opened, rather than because someone acted:
 * the ADP popup relay, one-time storage and analytics work, and the navigation away
 * once the session is live.
 *
 * Returns true once a live session means this page is on its way out, so the caller can skip
 * rendering a form the visitor would only see flash past.
 */
export const useSigninBootstrap = ({ queryParams, search, redirectUrl, initialEmail }: IUseSigninBootstrapParams): boolean => {
  const history = useHistory()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authState } = useAppSelector(selectAuthStateSlice)

  useEffect(() => {
    if (isAdpCallbackPopup(queryParams)) {
      BrowserRuntime().postOpenerMessage('adp-auth', queryParams.code)
      window.close()
      return
    }

    setSigninEmail(initialEmail)
    trackSignin('SIGN_IN_PAGE_VIEWED')
    document.body.classList.remove('flow-b-theme')
    localStorage.removeItem('vtCreateUser')
    localStorage.removeItem('vtCreateCompany')
    localStorage.removeItem('vtSelectedUsers')

    if (queryParams.subscriptionPlan) {
      localStorage.setItem('vtSubscriptionPlan', queryParams.subscriptionPlan)
    }
    saveSubscriptionPeriodFromQuery(queryParams.subscriptionPeriod)

    setCrispSessionInfo([CrispSegmentsEnum.chat, CrispSegmentsEnum.support, CrispSegmentsEnum.dashboard, CrispSegmentsEnum.onLoginPage], { loginScreen: '' })

    // Normalise the address bar once. Other routes render this same page, and the old
    // page re-pushed on every store change, stacking duplicate history entries.
    if (!history.location.pathname.startsWith(FrontendUrls.signin)) {
      history.replace(FrontendUrls.signin + search)
    }
  }, [])

  // One place decides where a live session goes. The old page ran two competing
  // redirects here and the role-agnostic one usually won the race.
  useEffect(() => {
    if (authState !== AuthStateEnum.signedIn) {
      return
    }
    if (authUser.id) {
      history.push(queryParams.redirect || getDashboardUrl(authUser.role, search))
      return
    }
    history.push(redirectUrl)
  }, [authState, authUser.id])

  // Derived while rendering, so the caller knows before the first paint. The navigation itself
  // stays in the effect above, where it belongs.
  return authState === AuthStateEnum.signedIn || isSessionStillResolving(authState)
}
